/**
 * Are we making money over the window picked, and more than before?
 *
 * Every USDC move between a player and the house, leaving out players moving
 * their own money (deposits, withdrawals). `accounts.selectors.bank.profit`
 * explains why that, and not the fee kept on death, is the profit.
 */
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Header, Stat, StatLabel, StatNumber } from '~/shared/ui'
import { useZone } from '~/team_tools/components/timezone'
import { CALENDAR, PeriodFigures } from './figures'
import { periodKey, usePeriod, useWindowParams } from './period'

type Figure = {
    in: number
    out: number
    net: number
    lines: Record<string, number>
    previous?: number
}

type Profit = Record<string, Figure> & { lines?: { key: string; label: string }[] }

const IN_COLOUR = 'rgb(34, 197, 94)'
const OUT_COLOUR = '#f43f5e'
const NET_COLOUR = '#fff'

const usd = (value: number) =>
    `${value < 0 ? '−' : ''}$${Math.abs(Number(value)).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

const DOLLAR_AXIS = {
    scales: {
        y: {
            // Net goes below zero on a losing day, so no floor at 0 here.
            ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: (value: number) => `$${value}` },
            grid: { color: 'rgba(255,255,255,0.06)' },
        },
    },
}

/** "Up $120.00 on the 3 days before" -- or nothing, for All time. */
const comparison = (figure?: Figure) => {
    if (!figure || figure.previous === undefined) return undefined
    const change = Number(figure.net) - Number(figure.previous)
    if (change === 0) return 'Same as the period before'
    return `${change > 0 ? 'Up' : 'Down'} ${usd(Math.abs(change))} on the period before (${usd(figure.previous)})`
}

export const Profit = () => {
    const [zone] = useZone()
    const window = useWindowParams()
    const period = usePeriod() ?? 'month'
    const [data, setData] = useState<Profit>()
    const [history, setHistory] = useState<Record<string, [number, number]>>({})

    useEffect(() => {
        let live = true
        setData(undefined)
        setHistory({})
        jsonrpc('statistic.profit', {
            periods: ['total', 'today', 'week', 'month'].concat(window.custom ? ['custom'] : []),
            tz: zone,
            ...window.figure,
        }).then((res) => live && setData(res.result))
        jsonrpc('statistic.profit.history', { ...window.history, tz: zone }).then(
            (res) => live && setHistory(res.result?.history || {})
        )
        return () => {
            live = false
        }
    }, [zone, window.key])

    const figure = data?.[periodKey(period)]
    const series = (pick: (row: [number, number]) => number) =>
        Object.fromEntries(Object.entries(history).map(([day, row]) => [day, pick(row)]))

    return (
        <Box className='w-full text-center gap-2'>
            <Header>Profit</Header>
            <small className='text-white/50'>
                Every USDC move between players and the house: spawn fees and sales in, winnings, prizes, refunds and
                USDC given away out. Deposits and withdrawals are players moving their own money, so they do not count.
                Airdrop only costs anything once it pays for a spawn whose kills are paid in USDC, and that shows up in
                winnings. Servers, card fees and wages are not in the database, so this is before those.
            </small>

            <PeriodFigures
                periods={CALENDAR}
                loading={data === undefined}
                value={(key) => (data?.[key] ? usd(data[key].net) : undefined)}
                note={(key) => comparison(data?.[key])}
                chart={{
                    lines: [
                        { label: 'In', colour: IN_COLOUR, history: series(([i]) => Number(i)) },
                        { label: 'Out', colour: OUT_COLOUR, history: series(([, o]) => Number(o)) },
                        { label: 'Net', colour: NET_COLOUR, history: series(([i, o]) => Number(i) - Number(o)) },
                    ],
                    options: DOLLAR_AXIS,
                }}
            />

            {/* Where the net came from, over the same window. Coloured by which
                way the money went, since a line can go either way -- winnings
                revoked in a quiet week come back in. */}
            <section className='grid w-full grid-cols-2 gap-2 pt-2 sm:grid-cols-3'>
                {(data?.lines ?? []).map(({ key, label }) => {
                    const value = figure ? Number(figure.lines[key]) : undefined
                    return (
                        <Stat.Gray key={key} loading={figure === undefined} className='py-2'>
                            <StatLabel
                                className={twMerge(
                                    !value ? 'text-white/40' : value > 0 ? 'text-green-400' : 'text-rose-400'
                                )}
                            >
                                {label}
                            </StatLabel>
                            <StatNumber className='text-base font-bold'>
                                {value !== undefined ? `${value > 0 ? '+' : ''}${usd(value)}` : undefined}
                            </StatNumber>
                        </Stat.Gray>
                    )
                })}
            </section>
        </Box>
    )
}
